import type { IMatch } from '@/interface/IMatch';
import type { IMarketGroup, IOdds, IPriceOption } from '@/interface/IOdds';
import { BetTypeGroup, MarketType, SportType } from '@/interface/enum';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { isNewBetType } from '@/libraries/marketGroupHelper';
import { isFastMarket } from '@/libraries/oddsDisplay';

const optionNameLookup: Partial<Record<MarketType, Record<string, string>>> = {
    [MarketType.Handicap]: { h: 'odds_option_home', a: 'odds_option_away' },
    [MarketType.FH_Handicap]: { h: 'odds_option_home', a: 'odds_option_away' },
    [MarketType.OverUnder]: { h: 'odds_option_over', a: 'odds_option_under' },
    [MarketType.FH_OverUnder]: { h: 'odds_option_over', a: 'odds_option_under' },
    [MarketType.OddEven]: { h: 'odds_option_odd', a: 'odds_option_even' },
    [MarketType.FH_OddEven]: { h: 'odds_option_odd', a: 'odds_option_even' },
    [MarketType._1X2]: { 1: 'odds_option_home', X: 'odds_option_draw', 2: 'odds_option_away' },
    [MarketType.FH_1X2]: { 1: 'odds_option_home', X: 'odds_option_draw', 2: 'odds_option_away' },
    [MarketType.MoneyLine]: { 1: 'odds_option_home', 2: 'odds_option_away' },
    [MarketType.HTFT]: {
        HH: 'odds_option_htft_hh',
        HD: 'odds_option_htft_hd',
        HA: 'odds_option_htft_ha',
        DH: 'odds_option_htft_dh',
        DD: 'odds_option_htft_dd',
        DA: 'odds_option_htft_da',
        AH: 'odds_option_htft_ah',
        AD: 'odds_option_htft_ad',
        AA: 'odds_option_htft_aa',
    },
    [MarketType.TotalGoal]: {
        '0-1': 'odds_option_tg_0_1',
        '2-3': 'odds_option_tg_2_3',
        '4-6': 'odds_option_tg_4_6',
        '7-OVER': 'odds_option_tg_7_up',
    },
    [MarketType.DoubleChance]: {
        'X2': 'odds_option_dc_2x',
        '12': 'odds_option_dc_12',
        '1X': 'odds_option_dc_1x',
    },
    [MarketType.FGLG]: {
        HF: 'odds_option_fglg_hf',
        HL: 'odds_option_fglg_hl',
        AF: 'odds_option_fglg_af',
        AL: 'odds_option_fglg_al',
        NG: 'odds_option_fglg_ng',
    },
};

const optionNameShortLookup: Partial<Record<MarketType, Record<string, string>>> = {
    [MarketType.Handicap]: { h: 'odds_option_home_short', a: 'odds_option_away_short' },
    [MarketType.FH_Handicap]: { h: 'odds_option_home_short', a: 'odds_option_away_short' },
    [MarketType.OverUnder]: { h: 'odds_option_over_short', a: 'odds_option_under_short' },
    [MarketType.FH_OverUnder]: { h: 'odds_option_over_short', a: 'odds_option_under_short' },
    [MarketType.OddEven]: { h: 'odds_option_odd_short', a: 'odds_option_even_short' },
    [MarketType.FH_OddEven]: { h: 'odds_option_odd_short', a: 'odds_option_even_short' },
    [MarketType._1X2]: { 1: 'odds_option_home_short', X: 'odds_option_draw_short', 2: 'odds_option_away_short' },
    [MarketType.FH_1X2]: { 1: 'odds_option_home_short', X: 'odds_option_draw_short', 2: 'odds_option_away_short' },
    [MarketType.MoneyLine]: { 1: 'odds_option_home_short', 2: 'odds_option_away_short' },
    [MarketType.HTFT]: {
        HH: 'odds_option_htft_hh',
        HD: 'odds_option_htft_hd',
        HA: 'odds_option_htft_ha',
        DH: 'odds_option_htft_dh',
        DD: 'odds_option_htft_dd',
        DA: 'odds_option_htft_da',
        AH: 'odds_option_htft_ah',
        AD: 'odds_option_htft_ad',
        AA: 'odds_option_htft_aa',
    },
    [MarketType.TotalGoal]: {
        '0-1': 'odds_option_tg_0_1',
        '2-3': 'odds_option_tg_2_3',
        '4-6': 'odds_option_tg_4_6',
        '7-OVER': 'odds_option_tg_7_up',
    },
    [MarketType.DoubleChance]: {
        'X2': 'odds_option_dc_2x',
        '12': 'odds_option_dc_12',
        '1X': 'odds_option_dc_1x',
    },
    [MarketType.FGLG]: {
        HF: 'odds_option_fglg_hf',
        HL: 'odds_option_fglg_hl',
        AF: 'odds_option_fglg_af',
        AL: 'odds_option_fglg_al',
        NG: 'odds_option_fglg_ng',
    },
};

const fastMarketOptionLookup: Record<string, string> = {
    h: 'odds_option_goal',
    a: 'odds_option_nogoal',
};

export enum OptionDisplayType {
    MarketTypeShortName,
    MarketTypeName,
    TeamName,
}

interface IOptionWithTemplateConfig{
    betTypeGroup: BetTypeGroup;
    type: MarketType;
}

const optionWithTemplateMarkets: IOptionWithTemplateConfig[] = [
    { betTypeGroup: BetTypeGroup.Basketball_FirstHalfMoneyLine, type: MarketType.MoneyLine },
    { betTypeGroup: BetTypeGroup.Baseball_HomeTeamTotalRuns, type: MarketType.OverUnder },
    { betTypeGroup: BetTypeGroup.Baseball_AwayTeamTotalRuns, type: MarketType.OverUnder },
];

export function getBetOptionNameForMainMarket(match: IMatch, odds: IOdds, option: IPriceOption): string {
    const isOptionWithTemplate = optionWithTemplateMarkets.some(
        condition => condition.betTypeGroup === odds.marketGroup.id && condition.type === odds.marketType,
    );
    const translationKey = optionNameShortLookup[odds.marketType]?.[option.option] ?? '';
    return isOptionWithTemplate ? i18n.t(translationKey) : getBetOptionName(match, odds, option, OptionDisplayType.MarketTypeShortName);
}

export function getMainMarketTitle(match: IMatch, betTypeGroup: BetTypeGroup, marketType: MarketType): string {
    if (betTypeGroup === BetTypeGroup.Unknown && marketType === MarketType.Unknown) return '';

    const isSpecialMainBetTypeTitle = [SportType.Volleyball, SportType.Badminton, SportType.TableTennis, SportType.Tennis].includes(match.sportType) && betTypeGroup === BetTypeGroup.Main;
    const isSpecialFHTitle = match.sportType === SportType.Baseball && [MarketType.FH_Handicap, MarketType.FH_OverUnder].includes(marketType);

    const prefix = (isSpecialMainBetTypeTitle || isSpecialFHTitle) ? `sport_type_${match.sportType}_` : '';

    return i18n.t(`odds_display_title_${prefix}bet_type_group_${betTypeGroup}_market_type_${marketType}_short`);
}

function getTeamNameDisplay(match: IMatch, odds: IOdds, priceOption: string): string | null {
    if (priceOption === 'h' || priceOption === '1') {
        return getTranslated(match?.homeTeam?.teamName);
    }
    if (priceOption === 'a' || priceOption === '2') {
        return getTranslated(match?.awayTeam?.teamName);
    }
    if (priceOption.toUpperCase() === 'X') {
        return i18n.t(optionNameLookup[odds.marketType]?.X ?? '').toString();
    }
    return null;
}

export function getBetOptionName(match: IMatch, odds: IOdds, { option: priceOption }: IPriceOption, optionDisplayName: OptionDisplayType): string {
    if (isFastMarket(odds.marketGroup.id)) {
        return i18n.t(fastMarketOptionLookup[priceOption]).toString();
    }
    const useTemplateMarketTypes = [
        MarketType.Handicap,
        MarketType.FH_Handicap,
        MarketType.OverUnder,
        MarketType.FH_OverUnder,
        MarketType._1X2,
        MarketType.FH_1X2,
        MarketType.MoneyLine,
    ];
    if (isNewBetType(odds.marketGroup.id) && useTemplateMarketTypes.includes(odds.marketType) && isExtensionTemplate(odds.marketGroup.homeTemplate)) {
        return getNewBetTypeBetOptionDisplayName(match, priceOption, odds.marketGroup);
    }

    switch (odds.marketType) {
        case MarketType.LiveScore:
        case MarketType.FH_LiveScore:
            return getAsian1X2OptionDisplayName(match, priceOption, odds.marketType, odds.eventResult.liveHomeScore, odds.eventResult.liveAwayScore);
        case MarketType.CorrectScore:
        case MarketType.FH_CorrectScore:
        case MarketType.SH_CorrectScore:
            return getCorrectScoreOptionName(priceOption, odds.isLive, odds.eventResult.liveHomeScore, odds.eventResult.liveAwayScore);
        case MarketType.Handicap:
        case MarketType.FH_Handicap:
        case MarketType._1X2:
        case MarketType.FH_1X2:
        case MarketType.MoneyLine:
            if (optionDisplayName === OptionDisplayType.TeamName) {
                const teamName = getTeamNameDisplay(match, odds, priceOption);
                if (teamName) {
                    return teamName;
                }
            }
            return getOptionNameByLookupNew(optionDisplayName, odds.marketType, priceOption);
        default:
            return getOptionNameByLookupNew(optionDisplayName, odds.marketType, priceOption);
    }
}

function getNewBetTypeBetOptionDisplayName(match: IMatch, option: string, marketGroup: IMarketGroup): string {
    const h = replaceTemplateWithMatch(marketGroup, marketGroup.homeTemplate);
    const a = replaceTemplateWithMatch(marketGroup, marketGroup.awayTemplate);

    switch (option) {
        case 'h':
        case '1':
            return h;
        case 'a':
        case '2':
            return a;
        case 'X':
            return i18n.t('odds_option_draw').toString();
        default:
            return '';
    }
}

function replaceTemplateWithMatch(marketGroup: IMarketGroup, template: string) {
    const homeExtension = getTranslated(marketGroup.homeExtension);
    const awayExtension = getTranslated(marketGroup.awayExtension);
    return template
        .replace(/_/g, '')
        .replace(/{home}/g, i18n.t('odds_option_home').toString())
        .replace(/{away}/g, i18n.t('odds_option_away').toString())
        .replace(/{homeext}/ig, homeExtension)
        .replace(/{awayext}/ig, awayExtension);
}

function getOptionNameByLookupNew(optionDisplayName: OptionDisplayType, marketType: MarketType, option: string) {
    const translationKey = optionDisplayName === OptionDisplayType.MarketTypeShortName ? optionNameShortLookup[marketType]?.[option] ?? '' : optionNameLookup[marketType]?.[option] ?? '';
    return i18n.t(translationKey).toString();
}

export function isExtensionTemplate(template: string): boolean {
    return template.match(/{(homeext|awayext)}/ig) !== null;
}

function getCorrectScoreOptionName(
    option: string,
    isLive: boolean,
    liveHomeScore: number,
    liveAwayScore: number,
) {
    if (option === 'O') {
        return i18n.t('odds_option_cs_others').toString();
    }

    let homeScore = parseInt((option.split(':')[0]));
    let awayScore = parseInt((option.split(':')[1]));
    if (isLive) {
        homeScore += liveHomeScore;
        awayScore += liveAwayScore;
    }

    return i18n.t('odds_option_cs_score', { home: homeScore, away: awayScore }).toString();
}

function getAsian1X2OptionDisplayName(
    match: IMatch,
    option: string,
    marketType: number,
    liveHomeScore: number,
    liveAwayScore: number,
): string {
    const translationKey = getAsian1X2OptionTranslationKey(option, marketType, liveHomeScore, liveAwayScore);
    const extraInfo = translationKey.split(';');
    const homeTeam = getTranslated(match?.homeTeam?.teamName);
    const awayTeam = getTranslated(match?.awayTeam?.teamName);

    const a1x2Info = {
        team: extraInfo[1] === 'home' ? homeTeam : awayTeam,
        goals: extraInfo[2],
    };
    return i18n.t(extraInfo[0], a1x2Info).toString();
}

const asian1X2TranslationKeyRules = {
    draw: {
        1: () => 'odds_option_a1x2_teamWins;home',
        X: (_goals: number, timeSuffix: string) => `odds_option_a1x2_draw_${timeSuffix};`,
        2: () => 'odds_option_a1x2_teamWins;away',
    },
    homeLeading: {
        1: (goals: number) => `odds_option_a1x2_teamWinsByMargin;home;${goals + 1}`,
        X: (goals: number) => `odds_option_a1x2_teamWinsByExactly;home;${goals}`,
        2: (goals: number, timeSuffix: string) => (goals === 1
            ? `odds_option_a1x2_teamWinsOrDraw_${timeSuffix};away;${goals}`
            : `odds_option_a1x2_teamLosesByMargin;away;${goals - 1}`),
    },
    awayLeading: {
        1: (goals: number, timeSuffix: string) => (goals === 1
            ? `odds_option_a1x2_teamWinsOrDraw_${timeSuffix};home;${goals}`
            : `odds_option_a1x2_teamLosesByMargin;home;${goals - 1}`),
        X: (goals: number) => `odds_option_a1x2_teamWinsByExactly;away;${goals}`,
        2: (goals: number) => `odds_option_a1x2_teamWinsByMargin;away;${goals + 1}`,
    },
};

function getAsian1X2OptionTranslationKey(
    option: string,
    marketType: number,
    liveHomeScore: number,
    liveAwayScore: number,
): string {
    const isHalfTime = marketType === MarketType.FH_LiveScore;
    const goals = Math.abs(liveHomeScore - liveAwayScore);
    const timeSuffix = isHalfTime ? 'fh' : 'ft';

    let normalizedOption: '1' | 'X' | '2' | null = null;
    if (option === '1' || option === 'LS_1') normalizedOption = '1';
    else if (option === 'x' || option === 'LS_X') normalizedOption = 'X';
    else if (option === '2' || option === 'LS_2') normalizedOption = '2';

    if (!normalizedOption) {
        return option;
    }

    let rules;
    if (liveHomeScore === liveAwayScore) {
        rules = asian1X2TranslationKeyRules.draw;
    } else if (liveHomeScore > liveAwayScore) {
        rules = asian1X2TranslationKeyRules.homeLeading;
    } else {
        rules = asian1X2TranslationKeyRules.awayLeading;
    }

    return rules[normalizedOption](goals, timeSuffix);
}
